import { GET } from "../config/axios.config";
import { withGeo } from "./geo";
import {
  isOfferFood,
  normalizeCategories,
  normalizeFood,
  normalizeFoods,
  normalizeKitchen,
  normalizeKitchens,
  normalizeRecipe,
  normalizeRecipes,
} from "./normalizers";

/**
 * Shape every paginated list into what the infinite-scroll pages need.
 *
 * `filtered` says whether a client-side filter narrowed this page — when it
 * did, the server's `total` no longer describes what the user can see, so the
 * pages fall back to counting what they have actually loaded.
 */
const toPage = (res, items, { page, limit, filtered = false } = {}) => {
  const pagination = res?.pagination || {
    total: items.length,
    page,
    limit,
    totalPages: 1,
  };
  return {
    items,
    pagination,
    filtered,
    // Based on the raw page size, not the filtered length: a page that filters
    // down to nothing may still be followed by pages that don't.
    hasMore: pagination.page < pagination.totalPages,
    nextPage: pagination.page + 1,
  };
};

/* ── Food ─────────────────────────────────────────────────────────────── */

/**
 * @param {object} options
 * @param {"ready-to-eat"|"pre-order"|"e-shop"|"offer"|"all"} [options.foodType]
 *   "offer" has no server-side equivalent (see normalizers.FOOD_TYPES), so it
 *   is applied as a client-side filter over the returned page.
 */
export const fetchFoodsPage = async ({
  foodType,
  categoryId,
  chefId,
  chefIds,
  search,
  minPrice,
  maxPrice,
  mealType,
  tag,
  page = 1,
  limit = 20,
  geo = true,
} = {}) => {
  const wantsOffers = foodType === "offer";
  const params = {
    page,
    limit,
    ...(search ? { search } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(chefId ? { chefId } : {}),
    ...(mealType ? { mealType } : {}),
    ...(tag ? { tag } : {}),
    ...(minPrice != null ? { minPrice } : {}),
    ...(maxPrice != null ? { maxPrice } : {}),
    ...(!wantsOffers && foodType && foodType !== "all" ? { foodType } : {}),
  };

  const res = await GET("/food", { params: geo ? withGeo(params) : params });
  let foods = normalizeFoods(res?.data);

  const multiChef = Array.isArray(chefIds) && chefIds.length > 0;
  if (wantsOffers) foods = foods.filter(isOfferFood);
  // The API filters by a single chefId; the "kitchens" checkbox filter sends
  // several, so that narrowing happens here over the fetched page.
  if (multiChef) {
    const allowed = new Set(chefIds);
    foods = foods.filter((f) => allowed.has(f.chefId));
  }
  return toPage(res, foods, { page, limit, filtered: wantsOffers || multiChef });
};

/** Single-page convenience for the home carousels, which never paginate. */
export const fetchFoods = async (options) =>
  (await fetchFoodsPage(options)).items;

export const fetchFoodById = async (foodId) => {
  const res = await GET(`/food/${foodId}`);
  return normalizeFood(res?.data?.food);
};

/* ── Kitchens (chefs) ─────────────────────────────────────────────────── */

export const fetchKitchensPage = async ({
  search,
  categoryId,
  isOpen,
  page = 1,
  limit = 20,
  geo = true,
} = {}) => {
  const params = {
    page,
    limit,
    ...(search ? { search } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(isOpen != null ? { isOpen: String(isOpen) } : {}),
  };
  const res = await GET("/chef/public", {
    params: geo ? withGeo(params) : params,
  });
  return toPage(res, normalizeKitchens(res?.data), { page, limit });
};

export const fetchKitchens = async (options) =>
  (await fetchKitchensPage(options)).items;

/**
 * The kitchen profile page renders the kitchen *and* its menu, but v2 splits
 * those across two endpoints — so they are fetched together and merged into the
 * single object the page was written against.
 */
export const fetchKitchenById = async (kitchenId) => {
  const [chefRes, foods] = await Promise.all([
    GET(`/chef/public/${kitchenId}`),
    fetchFoods({ chefId: kitchenId, limit: 100, geo: false }).catch(() => []),
  ]);

  const kitchen = normalizeKitchen(chefRes?.data?.chef);
  if (!kitchen) return null;

  return {
    ...kitchen,
    foods: foods.map((food) => ({
      ...food,
      sellerId: {
        _id: kitchen._id,
        name: kitchen.name,
        kitchenName: kitchen.kitchenName,
        kitchenLogo: kitchen.kitchenLogo,
      },
    })),
    foodCount: foods.length,
  };
};

/* ── Categories & cuisines ────────────────────────────────────────────── */

export const fetchCategories = async () => {
  const res = await GET("/categories");
  return normalizeCategories(res?.data?.categories);
};

export const fetchCuisines = async () => {
  const res = await GET("/cuisines");
  return normalizeCategories(res?.data?.cuisines);
};

/* ── Recipes ──────────────────────────────────────────────────────────── */

/**
 * Recipe cards show the authoring kitchen, but the recipe DTO carries only a
 * `chefId`. One kitchen lookup hydrates a whole page of cards — cached briefly
 * so paging through recipes doesn't refetch the kitchen list every time.
 */
const KITCHEN_CACHE_TTL_MS = 60_000;
let kitchenIndexCache = { at: 0, byId: null };

const kitchenIndex = async () => {
  if (kitchenIndexCache.byId && Date.now() - kitchenIndexCache.at < KITCHEN_CACHE_TTL_MS) {
    return kitchenIndexCache.byId;
  }
  const kitchens = await fetchKitchens({ limit: 100, geo: false }).catch(() => []);
  const byId = Object.fromEntries(kitchens.map((k) => [k._id, k]));
  kitchenIndexCache = { at: Date.now(), byId };
  return byId;
};

const hydrateRecipeKitchens = async (recipes) => {
  const chefIds = [...new Set(recipes.map((r) => r.chefId).filter(Boolean))];
  if (!chefIds.length) return normalizeRecipes(recipes);
  return normalizeRecipes(recipes, await kitchenIndex());
};

export const fetchRecipesPage = async ({
  search,
  categoryId,
  cuisineId,
  mealType,
  difficulty,
  tag,
  page = 1,
  limit = 20,
} = {}) => {
  const res = await GET("/recipes", {
    params: {
      page,
      limit,
      ...(search ? { search } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(cuisineId ? { cuisineId } : {}),
      ...(mealType ? { mealType } : {}),
      ...(difficulty ? { difficulty } : {}),
      ...(tag ? { tag } : {}),
    },
  });
  const recipes = await hydrateRecipeKitchens(
    Array.isArray(res?.data) ? res.data : [],
  );
  return toPage(res, recipes, { page, limit });
};

export const fetchRecipes = async (options) =>
  (await fetchRecipesPage(options)).items;

/**
 * There is no chefId filter on the public recipe list, so the kitchen's own
 * recipes are picked out client-side — walking the pages rather than hoping
 * they all land in the first one.
 */
export const fetchRecipesByKitchen = async (kitchenId, { maxPages = 5 } = {}) => {
  const mine = [];
  let page = 1;
  for (let i = 0; i < maxPages; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await fetchRecipesPage({ page, limit: 100 });
    mine.push(...result.items.filter((recipe) => recipe.chefId === kitchenId));
    if (!result.hasMore) break;
    page = result.nextPage;
  }
  return mine;
};

export const fetchRecipeById = async (recipeId) => {
  const res = await GET(`/recipes/${recipeId}`);
  const recipe = res?.data?.recipe;
  if (!recipe) return null;
  if (!recipe.chefId) return normalizeRecipe(recipe);

  const kitchen = await GET(`/chef/public/${recipe.chefId}`)
    .then((r) => normalizeKitchen(r?.data?.chef))
    .catch(() => null);

  return normalizeRecipe(
    recipe,
    kitchen ? { [kitchen._id]: kitchen } : {},
  );
};

/* ── Search ───────────────────────────────────────────────────────────── */

/**
 * v2 has no `/search/global`; the header search box composes one out of the
 * public browse endpoints instead. Categories carry no search parameter, so
 * that tab is matched client-side over the (short) active category list.
 *
 * Keys match the dropdown's tabs: foods | kitchens | categories.
 */
export const globalSearch = async (query) => {
  const term = String(query || "").trim();
  if (!term) return { foods: [], kitchens: [], categories: [] };

  const [foods, kitchens, categories] = await Promise.all([
    fetchFoods({ search: term, limit: 8 }).catch(() => []),
    fetchKitchens({ search: term, limit: 8 }).catch(() => []),
    fetchCategories().catch(() => []),
  ]);

  const needle = term.toLowerCase();
  return {
    foods,
    kitchens,
    categories: categories
      .filter((c) => c?.name?.toLowerCase().includes(needle))
      .slice(0, 8),
  };
};
